<template lang="pug">
section.section(
    :class="classes"
    @click="editSection"
)
    include /components/Page/Sections/base.pug
    .content-wrappers(v-if="!isEmpty || $slots.default")
        slot
        template(v-if="!disabled")
            .preview(v-if="section.articles && section.articles.length")
                .content-wrapper
                    template(v-if="section.articles.length === 1") {{ $t('1 sub-article') }}
                    template(v-else) {{ $t('{count} sub-articles', { count: section.articles.length }) }}
            .pos-template-hint(v-else-if="isPosTemplate")
                i.el-icon-fa-info-circle.fas
                span {{ $t('Sub-Articles will be added from POS cart at send time') }}
            .error(v-else)
                el-alert(type="warning" :title="$t('No sub-articles selected')")

    el-drawer.full-height-drawer.visible-overflow(
        v-if="editable"
        :title="$t('Edit {section}', { section: section.name })"
        :visible.sync="scopedUI.form"
        :before-close="confirmDiscard(() => changedSince(drawer.sectionSnapshot, drawer.section))"
        :wrapper-closable="false"
        :append-to-body="true"
        size="920px"
        :modal="true"
        @closed="reset"
    )
        .drawer-body(v-loading="drawer.loading")
            el-tabs(
                v-if="!drawer.loading"
                v-model="drawer.tab"
                tab-position="top"
            )
                el-tab-pane(name="content" :label="$t('Content')")
                    .tab-content
                        .content-wrapper
                            .pos-template-content-hint(v-if="isPosTemplate")
                                i.el-icon-fa-info-circle.fas
                                .hint-text
                                    strong {{ $t('POS Template Mode') }}
                                    p {{ $t('Sub-Articles will be added automatically from the POS cart when the newsletter is sent.') }}
                                    p {{ $t('You can configure display settings in the Settings tab.') }}
                            .edit(v-else-if="edit")
                                paginated-items(
                                    v-if="drawer.section && drawer.section.articles && drawer.section.articles.length"
                                    :scope="pagination.scope"
                                    :type="pagination.type"
                                    :loading="pagination.loading"
                                    :items="pagination.items"
                                    :filters="pagination.filters"
                                    :show-filters="showFilters"
                                    :variables="pagination.variables"
                                    :all-columns="allColumns"
                                    :active-columns="activeColumns"
                                    :page-info="pagination.pageInfo"
                                    :sort="sort"
                                    @change-filter="changeFilter"
                                    @show-table-columns="changeTableColumns"
                                    @pagination-size-change="changePaginationSize"
                                    @pagination-current-change="changeCurrentPage"
                                    @change-sort="handleSortInputChange"
                                )
                                    template(slot="top")
                                        el-row.table-top(v-loading="pagination.loading" type="flex" justify="space-between")
                                            .left
                                                el-button.open-article-modal-button(
                                                    size="mini"
                                                    type="primary"
                                                    icon="el-icon-fa-plus fas"
                                                    @click="openArticleModal"
                                                )
                                                    | {{ $t('Add Sub-Articles') }}
                                            .right.hide-on-empty
                                                el-button(
                                                    plain
                                                    size="mini"
                                                    type="primary"
                                                    icon="el-icon-fa-filter fas"
                                                    @click="toggleFilterVisiblity"
                                                    :disabled="disable.showFilters || hasFilters"
                                                )
                                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                                    span(v-else) {{ $t('Show filters') }}
                                    template(v-slot:actions="{ item }")
                                        el-button.delete-button(
                                            v-if="! isArticleDeleted(item)"
                                            plain
                                            circle
                                            size="mini"
                                            type="danger"
                                            icon="el-icon-fa-trash-alt far"
                                            @click.stop.prevent="removeArticleFromSection(item)"
                                        )
                                        el-button.restore-button(
                                            v-else
                                            plain
                                            circle
                                            size="mini"
                                            type="info"
                                            icon="el-icon-fa-trash-restore fas"
                                            @click.stop.prevent="restoreArticleInSection(item)"
                                        )

                                    template(v-slot:status="{ value }")
                                        span {{ $t(value) }}

                                    template(v-slot:name="{ item, value }")
                                        template(v-if="item.name") {{ item.name }}
                                        span.gray(v-else) {{ item.product.name }}

                                    template(v-slot:description="{ item, value }")
                                        template(v-if="item.description" v-html="item.description")
                                        span.gray(v-else v-html="item.product.description")

                                    template(v-slot:properties="{ value: propertyPivots }")
                                        span(v-if="propertyPivots && propertyPivots.length")
                                            property-pivot-values(
                                                v-for="propertyPivot in propertyPivots"
                                                :key="propertyPivot.id"
                                                :property="propertyPivot.property"
                                                :value-options="[propertyPivot.valueOption]"
                                            )
                                        small.gray(v-else)
                                            i {{ $t('no properties') }}

                                    template(v-slot:main_price_net="{ value }")
                                        div
                                            template(v-if="value") {{ value | moneyFormat(defaultCustomerGroupCurrency) }}
                                            span.gray(v-else) {{ priceNetValue | moneyFormat(defaultCustomerGroupCurrency) }}
                                    template(v-slot:main_price_gross="{ value }")
                                        div
                                            template(v-if="value") {{ value | moneyFormat(defaultCustomerGroupCurrency) }}
                                            span.gray(v-else) {{ priceGrossValue | moneyFormat(defaultCustomerGroupCurrency) }}
                                    template(v-slot:sold_qty_sum="{ value }")
                                        span(v-show="value > 0") {{ value }}

                                .no-articles(v-else)
                                    el-alert(
                                        :title="$t('No sub-articles selected')"
                                        type="warning"
                                        effect="light"
                                        :closable="false"
                                    )
                                    el-button.open-article-modal-button(
                                        size="mini"
                                        type="primary"
                                        icon="el-icon-fa-plus fas"
                                        @click="openArticleModal"
                                    )
                                        | {{ $t('Add Sub-Articles') }}

                el-tab-pane(name="settings" :label="$t('Settings')")
                    .block-settings
                        .settings-wrap
                            include /components/Page/Sections/settings.pug
        .drawer-footer
            include /components/Page/Sections/section-drawer-footer.pug

        //- el-dialog.add-articles-dialog(
        //-     :title="$t('Add Articles')"
        //-     :visible.sync="modal.visibility"
        //-     :close-on-click-modal="false"
        //-     :modal-append-to-body="false"
        //-     top="10vh"
        //-     width="1536px"
        //-     @closed="resetModal"
        //- )

    el-drawer.article-list-drawer(
        :title="$t('Add Sub-Articles')"
        :visible.sync="modal.visibility"
        :wrapper-closable="false"
        :append-to-body="true"
        :modal="true"
        size="90%"
        @closed="resetModal"
    )
        .drawer-body(v-loading="modal.loading")
            el-form.articles-modal-form(
                ref="form"
                :model="modal.form"
                label-position="top"
                :rules="modal.rules"
                v-loading="modal.loading"

                @submit.prevent.native="addArticlesToSection"
            )
                transition(name="slide-vertical")
                    .modal-body-pagination(v-if="! modal.loading")
                        paginated-items(
                            :scope="modal.pagination.scope"
                            :type="modal.pagination.type"
                            :loading="modal.pagination.loading"
                            :items="modal.pagination.items"
                            :filters="modal.pagination.filters"
                            :show-filters="true"
                            :variables="modal.pagination.variables"
                            :all-columns="allColumns"
                            :active-columns="activeColumns"
                            :page-info="modal.pagination.pageInfo"
                            :sort="modalSort"
                            @change-filter="changeModalFilter"
                            @show-table-columns="changeModalTableColumns"
                            @pagination-size-change="changeModalPaginationSize"
                            @pagination-current-change="changeModalCurrentPage"
                            @change-sort="handleModalSortInputChange"
                            @click="toggleModalArticle"
                        )
                            template(v-slot:actions="{ item }")
                                template(v-if="isModalItemInSection(item)")
                                    el-button.delete-button(
                                        v-if="! isArticleDeleted(item)"
                                        plain
                                        circle
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-fa-trash-alt far"
                                        @click.stop.prevent="removeArticleFromSection(item)"
                                    )
                                    el-button.restore-button(
                                        v-else
                                        plain
                                        circle
                                        size="mini"
                                        type="success"
                                        icon="el-icon-fa-trash-restore fas"
                                        @click.stop.prevent="restoreArticleInSection(item)"
                                    )
                                el-switch(
                                    v-else
                                    :key="item.id"
                                    :value="isModalArticleSelected(item)"
                                    @change="enabled => toggleModalArticle(item)"
                                )
                            template(v-slot:status="{ value }")
                                span {{ $t(value) }}

                            template(v-slot:name="{ item, value }")
                                template(v-if="item.name") {{ item.name }}
                                span.gray(v-else) {{ item.product.name }}

                            template(v-slot:description="{ item, value }")
                                template(v-if="item.description" v-html="item.description")
                                span.gray(v-else v-html="item.product.description")

                            template(v-slot:properties="{ value: propertyPivots }")
                                span(v-if="propertyPivots && propertyPivots.length")
                                    property-pivot-values(
                                        v-for="propertyPivot in propertyPivots"
                                        :key="propertyPivot.id"
                                        :property="propertyPivot.property"
                                        :value-options="[propertyPivot.valueOption]"
                                    )
                                small.gray(v-else)
                                    i {{ $t('no properties') }}

                            template(v-slot:main_price_net="{ value }")
                                div
                                    template(v-if="value") {{ value | moneyFormat(defaultCustomerGroupCurrency) }}
                                    span.gray(v-else) {{ priceNetValue | moneyFormat(defaultCustomerGroupCurrency) }}
                            template(v-slot:main_price_gross="{ value }")
                                div
                                    template(v-if="value") {{ value | moneyFormat(defaultCustomerGroupCurrency) }}
                                    span.gray(v-else) {{ priceGrossValue | moneyFormat(defaultCustomerGroupCurrency) }}
                            template(v-slot:sold_qty_sum="{ value }")
                                span(v-show="value > 0") {{ value }}

        //- template(slot="footer")
        .drawer-footer
            el-button(
                round
                size="mini"
                icon="el-icon-fa-times fas"
                @click="closeModal"
            ) {{ $t('Dismiss') }}
            el-button(
                round
                size="mini"
                :disabled="modal.loading"
                type="primary"
                icon="el-icon-fa-check fas"
                @click="addArticlesToSection"
            ) {{ $t('Ok') }}
</template>

<i18n>
{
    "en": {
        "Edit {section}": "Edit {section}",
        "POS Template Mode": "POS Template Mode",
        "Sub-Articles will be added automatically from the POS cart when the newsletter is sent.": "Sub-Articles will be added automatically from the POS cart when the newsletter is sent.",
        "You can configure display settings in the Settings tab.": "You can configure display settings in the Settings tab.",
        "{count} sub-articles": "{count} sub-articles"
    },
    "de": {
        "Edit {section}": "{section} bearbeiten",
        "Content": "Inhalt",
        "Settings": "Einstellungen",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "No sub-articles selected": "Es sind keine Unterartikel ausgewählt",
        "Sub-Articles will be added from POS cart at send time": "Unterartikel werden beim Versenden aus dem POS-Warenkorb hinzugefügt",
        "POS Template Mode": "POS-Vorlagen-Modus",
        "Sub-Articles will be added automatically from the POS cart when the newsletter is sent.": "Unterartikel werden beim Versenden des Newsletters automatisch aus dem POS-Warenkorb hinzugefügt.",
        "You can configure display settings in the Settings tab.": "Sie können die Anzeigeeinstellungen im Reiter Einstellungen konfigurieren.",
        "Sub-Article list could not be fetched.": "Unterartikelliste konnte nicht abgerufen werden.",
        "Add Sub-Articles": "Unterartikel hinzufügen",
        "1 sub-article": "1 Unterartikel",
        "{count} sub-articles": "{count} Unterartikel",
        "Margin top": "Abstand oben",
        "Margin bottom": "Abstand unten",
        "none": "Ohne",
        "small": "Klein",
        "medium": "Mittel",
        "large": "Groß",
        "default": "Standard",
        "custom": "Benutzerdefiniert",
        "Everybody": "Jedem",
        "Members": "Mitgliedern",
        "Guests": "Gästen",
        "Any": "Alle",
        "Tags": "Tags",
        "Select tags": "Tags auswählen",
        "Accessible by": "Zugänglich von",
        "desktop": "Desktop",
        "tablet": "Tablet",
        "mobile": "Telefon",
        "Visible on":"Anzeigen auf",
        "Element is displayed on":"Inhaltselement <br> wird dargestellt auf"
    }
}
</i18n>

<script>
import _ from 'lodash';

import store from '@/store';
import pinia from '@/pinia';
import { useAuthStore, useSetupStore } from '@/stores'

import ArticleApi from '@/extensions/shop/store/api/article';

import { checkRestrictions } from '@/helpers';
import Section from '@/components/Page/Sections/Section';
import PaginatedItems from '@/components/PaginatedItems';
import PropertyPivotValues from '@/extensions/shop/components/Products/PropertyPivotValues';

import { adminClient as apolloClient } from '@/store/apollo';

export default {
    name: 'Articles',
    extends: Section,
    data: () => ({
        modal: {
            visibility: false,
            loading: false,
            rules: {},
            form: {
                articles: []
            },
            pagination: {
                type: 'shop_cp_section_article',
                scope: 'ex',
                loading: false,
                items: [],
                filters: {},
                variables: {
                    paginationSizes: [10, 20, 50]
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 20,
                    total: 0
                }
            }
        },
        allColumns: [],
        activeColumns: [],
        pagination: {
            type: 'shop_cp_section_article',
            scope: 'ex',
            showFilters: false,
            loading: false,
            items: [],
            filters: {},
            variables: {
                paginationSizes: [10, 20, 50]
            },
            pageInfo: {
                currentPage: 1,
                lastPage: 1,
                perPage: 10,
                total: 0
            }
        },
        disable: {
            showFilters: false
        }
    }),
    components: {
        PaginatedItems,
        PropertyPivotValues,
    },
    watch: {
        'section.articles'(current, previous) {
            if (current.length !== previous.length) {
                // this.debouncedRefetchPagination();
                this.refetchPagination();
            }
        }
    },
    computed: {
        editable() {
            return checkRestrictions('ex:shop');
        },
        isPosTemplate() {
            // Check if this section is inside a newsletter that is marked as POS template
            return _.get(this.page, 'is_pos_template', false);
        },
        isEmpty() {
            // let empty = !this.$slots.default
            let empty = true

            if (empty && this.section.articles && this.section.articles.length) empty = false

            // if (empty && this.section.informations && this.section.informations.length) empty = false
            // if (empty && this.section.eventCategories && this.section.eventCategories.length) empty = false
            // if (empty && this.section.shopCategories && this.section.shopCategories.length) empty = false
            // if (empty && this.section.articles && this.section.articles.length) empty = false

            // const contents = _.get(this.section, 'contents') || []
            // contents.forEach(content => {
            //     if (! empty) return
            //     if (content.addresses && content.addresses.length) empty = false
            //     if (content.images && content.images.length) empty = false
            //     if (content.media && content.media.length) empty = false
            //     if (content.title || content.sub_title || content.text) empty = false
            // })

            return empty
        },
        activeShop() {
            return this.activeCms.shop;
        },
        showFilters() {
            return this.hasFilters || _.get(this.pagination, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            let filters = _.cloneDeep(this.pagination.filters);
            delete filters.shop;
            delete filters.ids;
            return filters;
        },
        user() {
            return useAuthStore().user;
        },
        sort() {
            var variables = this.pagination.variables;
            var defaultSort = this.pagination.defaultSort || { field: 'id', direction: 'desc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        },
        modalSort() {
            var variables = this.modal.pagination.variables;
            var defaultSort = this.modal.pagination.defaultSort || { field: 'id', direction: 'desc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        }
    },
    methods: {
        reset() {
            this.resetDrawer()
            this.pagination = {
                type: 'shop_cp_section_article',
                scope: 'ex',
                showFilters: false,
                loading: false,
                items: [],
                filters: {},
                variables: {
                    paginationSizes: [10, 20, 50]
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 10,
                    total: 0
                }
            }

            this.$nextTick(this.refetchPagination)
        },
        async fetchArticlesPaginated(payload) {
            return ArticleApi.fetchArticlesPaginated(payload);
        },
        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.allColumns, column => {
                let allowed = _.find(activeColumnKeys, el => column.key === el.key);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.activeColumns = _.cloneDeep(activeColumns);
            }
        },
        openArticleModal() {
            this.modal.loading = true
            this.modal.visibility = true;

            // set shop filter
            if (this.activeShop) {
                const key = 'shop';
                const value = {
                    key,
                    match: this.activeShop.id,
                    like: '',
                    from: '',
                    to: '',
                    in: []
                };
                this.$set(this.modal.pagination.filters, key, value);
            }

            this.refetchModalPagination();
        },

        refetchPagination() {
            const section = this.drawer.section || this.section

            this.pagination.loading = true;

            const articles = section.articles || [];

            const key = 'ids';
            const value = {
                key,
                match: '',
                like: '',
                from: '',
                to: '',
                in: articles.map(el => el.id)
            };
            this.$set(this.pagination.filters, key, value);

            this.fetchArticlesPaginated(this.pagination)
                .then(data => {
                    this.pagination.items = _.cloneDeep(data.items);
                    this.pagination.pageInfo = _.cloneDeep(data.pageInfo);
                    this.pagination.showFilters = _.get(data.config, 'showFilters') || false;
                    this.pagination.defaultSort = _.get(data.config, 'defaultSort') || null;

                    this.allColumns = _.cloneDeep(data.config.allColumns);
                    this.defineActiveColumns(data.config.activeColumnKeys);
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Sub-Article list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.pagination.loading = false;
                });
        },
        debouncedRefetchPagination: _.debounce(function() {
            // function scope is needed here
            this.refetchPagination();
        }, useSetupStore(pinia).debounce || 300),
        changeFilter(key, value, debounce = true) {
            this.$set(this.pagination.filters, key, value);
            this.pagination.pageInfo.currentPage = 1;
            if (debounce) this.debouncedRefetchPagination();
            else this.refetchPagination();
        },
        changeTableColumns(visibleColumns) {
            this.defineActiveColumns(visibleColumns.map(key => ({ key })));
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        changePaginationSize(itemsPerPage) {
            this.pagination.pageInfo.currentPage = 1;
            this.pagination.pageInfo.perPage = itemsPerPage;
            this.refetchPagination();
        },
        changeCurrentPage(currentPage) {
            this.pagination.pageInfo.currentPage = currentPage;
            this.refetchPagination();
        },
        handleSortInputChange(key, value) {
            this.$set(this.pagination.variables, 'order', key);
            this.$set(this.pagination.variables, 'direction', value);
            this.refetchPagination();
        },
        handleModalSortInputChange(key, value) {
            this.$set(this.modal.pagination.variables, 'order', key);
            this.$set(this.modal.pagination.variables, 'direction', value);
            this.refetchModalPagination();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            this.pagination.showFilters = value;

            this.saveFilterVisibility({
                scope: this.pagination.scope,
                key: this.pagination.type,
                value,
            }).finally(() => (this.disable.showFilters = false));
        },

        refetchModalPagination() {
            this.modal.pagination.loading = true;
            this.fetchArticlesPaginated(this.modal.pagination)
                .then(data => {
                    this.modal.pagination.items = data.items;
                    this.modal.pagination.pageInfo = _.cloneDeep(data.pageInfo);
                    // this.modal.pagination.showFilters = _.get(data.config, 'showFilters') || false;
                    this.modal.pagination.defaultSort = _.get(data.config, 'defaultSort') || null;

                    this.allColumns = _.cloneDeep(data.config.allColumns);
                    this.defineActiveColumns(data.config.activeColumnKeys);
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Sub-Article list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.modal.pagination.loading = false;
                    this.modal.loading = false;
                });
        },
        debouncedRefetchModalPagination: _.debounce(function() {
            // function scope is needed here
            this.modal.pagination.loading = true;
            this.refetchModalPagination();
        }, useSetupStore(pinia).debounce || 300),

        closeModal() {
            this.modal.visibility = false;
        },
        resetModal() {
            // TODO
            this.modal = {
                visibility: false,
                loading: false,
                rules: {},
                form: {
                    articles: []
                },
                pagination: {
                    type: 'shop_cp_section_article',
                    scope: 'ex',
                    loading: false,
                    items: [],
                    filters: {},
                    variables: {
                        paginationSizes: [10, 20, 50]
                    },
                    pageInfo: {
                        currentPage: 1,
                        lastPage: 1,
                        perPage: 20,
                        total: 0
                    }
                }
            };
        },
        changeModalFilter(key, value, debounce = true) {
            this.$set(this.modal.pagination.filters, key, value);
            this.modal.pagination.pageInfo.currentPage = 1;
            if (debounce) this.debouncedRefetchModalPagination();
            else this.refetchModalPagination();
        },
        changeModalTableColumns(visibleColumns) {
            this.defineActiveColumns(visibleColumns.map(key => ({ key })));
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        changeModalPaginationSize(itemsPerPage) {
            this.modal.pagination.loading = true;
            this.modal.pagination.pageInfo.currentPage = 1;
            this.modal.pagination.pageInfo.perPage = itemsPerPage;
            this.refetchModalPagination();
        },
        changeModalCurrentPage(currentPage) {
            this.modal.pagination.loading = true;
            this.modal.pagination.pageInfo.currentPage = currentPage;
            this.refetchModalPagination();
        },
        isModalArticleSelected(item) {
            return !!this.modal.form.articles.find(el => parseInt(el.id) === parseInt(item.id));
        },
        toggleModalArticle(item) {
            if (this.isModalItemInSection(item)) {
                if (this.isArticleDeleted(item)) {
                    // const payload = {
                    //     i: this.i,
                    //     // p: this.p,
                    //     s: this.s,
                    //     article: { id: item.id }
                    // };
                    // store.commit(`${this.module}/form/restoreArticleInSection`, payload);

                    this.restoreArticleInSection(item);
                } else {
                    // const payload = {
                    //     i: this.i,
                    //     // p: this.p,
                    //     s: this.s,
                    //     article: { id: item.id }
                    // };
                    // store.commit(`${this.module}/form/removeArticleFromSection`, payload);

                    this.removeArticleFromSection(item)
                }
            } else {
                const index = _.findIndex(this.modal.form.articles, el => parseInt(el.id) === parseInt(item.id));
                if (index >= 0) {
                    // remove
                    this.$delete(this.modal.form.articles, index);
                } else {
                    // add
                    this.$set(this.modal.form.articles, this.modal.form.articles.length, { id: item.id });
                    // this.modal.form.articles.push(item.id)
                }
            }
        },
        isModalItemInSection(item) {
            const section = this.drawer.section || this.section
            return !!section.articles.find(el => parseInt(el.id) === parseInt(item.id));
        },
        isArticleDeleted(item) {
            const section = this.drawer.section || this.section
            return !!section.articles.find(el => parseInt(el.id) === parseInt(item.id) && el.deleted === true);
        },
        removeArticleFromSection(article) {
            const section = this.drawer.section || this.section

            // this.$set(item, 'deleted', true)

            // const payload = {
            //     i: this.i,
            //     // p: this.p,
            //     s: this.s,
            //     article
            // };
            // store.commit(`${this.module}/form/removeArticleFromSection`, payload);

            const articles = section.articles || [];
            const p = _.findIndex(articles, el => parseInt(el.id) === parseInt(article.id));
            if (p >= 0) this.$set(section.articles[p], 'deleted', true);
        },
        restoreArticleInSection(article) {
            const section = this.drawer.section || this.section

            // this.$set(item, 'deleted', false)

            // const payload = {
            //     i: this.i,
            //     // p: this.p,
            //     s: this.s,
            //     article
            // };
            // store.commit(`${this.module}/form/restoreArticleInSection`, payload);

            const articles = section.articles || [];
            const p = _.findIndex(articles, el => parseInt(el.id) === parseInt(article.id));
            if (p >= 0) this.$delete(section.articles[p], 'deleted');
        },
        addArticlesToSection() {
            const section = this.drawer.section || this.section

            const initialArticles = _.cloneDeep(section.articles);
            const modalArticles = _.cloneDeep(this.modal.form.articles);

            // const payload = {
            //     i: this.i,
            //     // p: this.p,
            //     s: this.s,
            //     articles: [...modalArticles, ...initialArticles]
            // };
            // store.commit(`${this.module}/form/sectionArticles`, payload);

            this.$set(section, 'articles', [...modalArticles, ...initialArticles]);

            this.closeModal();

            // fetch section articles
            // TODO: fix deletion marks

            this.$nextTick(() => {
                this.refetchPagination();
            });
        },
        saveFilterVisibility(payload) {
            return useSetupStore(pinia).saveFilterVisibility(payload);
        }
    },
    mounted() {
        const section = this.drawer.section || this.section

        // set shop filter
        if (this.activeShop) {
            const key = 'shop';
            const value = {
                key,
                match: this.activeShop.id,
                like: '',
                from: '',
                to: '',
                in: []
            };
            this.$set(this.pagination.filters, key, value);
        }

        if (section.articles && section.articles.length) {
            this.refetchPagination();
        }
    }
};
</script>

<style lang="sass" scoped>
// #body .full-height-drawer .drawer-body .el-tabs__content
.viewports
    margin-right: 12px
    .el-tooltip
        margin: 0 3px
        color: #9ca3af
        .inner
            text-align: center
.edit .paginated-items
    background: none
    font-size: .85rem
    line-height: 1.3
    margin-left: -20px
    margin-right: -20px
    // margin-bottom: 10px
    :deep()
        tr
            th.actions,
            td.actions
                width: 60px
                min-width: 0
        .table-top
            padding-top: 0
            padding-left: 20px
            padding-right: 20px
            min-height: 0

.article-list-drawer .drawer-body
    height: calc(100vh - 134px)
    overflow: auto

.no-articles
    .el-alert
        margin-bottom: 1rem

.paginated-items:deep()
    tr span.gray
        color: lighten($color-grey-text, 10%)

.pos-template-hint
    display: flex
    align-items: center
    gap: 8px
    padding: 10px 14px
    background: #ecf5ff
    border: 1px solid #b3d8ff
    border-radius: 4px
    color: #409eff
    font-size: 13px
    margin: 10px 0
    i
        font-size: 14px

.pos-template-content-hint
    display: flex
    align-items: flex-start
    gap: 16px
    padding: 20px
    background: #ecf5ff
    border: 1px solid #b3d8ff
    border-radius: 6px
    color: #409eff
    margin: 10px 0
    i
        font-size: 24px
        margin-top: 2px
    .hint-text
        strong
            display: block
            font-size: 16px
            margin-bottom: 8px
        p
            font-size: 14px
            margin: 4px 0
            color: #606266
</style>
